import { useMemoizedFn } from 'ahooks';
import { Modal, message } from 'antd';
import 'cropperjs/dist/cropper.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactCropperElement } from 'react-cropper';
import Cropper from 'react-cropper';

import handleUpload from '@/utils/upload';

interface IProps {
  handleSetAvatarCb: (avatar: string) => void;
  cropperModalTitle?: string;
  businessType?: 'rizz';
  zIndex?: number;
}

const useAvatarCropper = ({ handleSetAvatarCb, cropperModalTitle, zIndex = 1000 }: IProps) => {
  const [uploadAvatarFile, setUploadAvatarFile] = useState<string>('');
  const [cropModalOpen, setCropModalOpen] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const cropperRef = useRef<ReactCropperElement>(null);
  const fileRef = useRef<File>();
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!cropModalOpen) {
      abortControllerRef.current?.abort('Cancel');
    }
  }, [cropModalOpen]);

  const onReady = () => {
    const cropper = cropperRef.current?.cropper;

    if (!cropper) return;

    const img = cropper.getImageData(); // 获取原图数据
    const container = cropper.getContainerData(); // 容器大小

    // 计算 contain 比例
    const fitRatio = Math.min(
      container.width / img.naturalWidth,
      container.height / img.naturalHeight
    );

    // 用 zoomTo（如果版本支持）
    if (typeof cropper.zoomTo === 'function') {
      cropper.zoomTo(fitRatio);
    } else {
      // 兼容：直接用 setCanvasData 手动调宽高
      const canvas = cropper.getCanvasData();
      const currentRatio = img.width / img.naturalWidth;
      const ratio = fitRatio / currentRatio;

      cropper.setCanvasData({
        ...canvas,
        width: canvas.width * ratio,
        height: canvas.height * ratio
      });
    }
  };

  // 文件选择处理
  const onAvatarSelect: React.ChangeEventHandler<HTMLInputElement> = useMemoizedFn((event) => {
    const fileList = event.target.files ?? [];

    if (fileList.length === 0) {
      return;
    }

    const file = fileList[0];

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      message.error('Only support png and jpeg, please try again.');

      return;
    }

    fileRef.current = file;

    const reader = new FileReader();

    reader.onload = () => {
      if (reader.result) {
        setUploadAvatarFile(reader.result as string);
      }
    };
    reader.readAsDataURL(file);
    setCropModalOpen(true);
  });

  // 头像上传处理
  const handleUploadAvatar = useMemoizedFn(() => {
    if (isUploading) return;

    setIsUploading(true);
    message.success('Uploading...');

    const canvas = cropperRef.current?.cropper.getCroppedCanvas();

    if (!canvas) {
      setIsUploading(false);

      return;
    }

    // 获取原始文件类型
    const originalFile = fileRef.current;
    const mimeType = originalFile?.type || 'image/png';

    const controller = new AbortController();
    const { signal } = controller;

    abortControllerRef.current = controller;

    canvas.toBlob((blob) => {
      if (!blob) {
        setIsUploading(false);
        message.error('Image processing failed');

        return;
      }

      const fileName = `avatar_${Date.now()}.${mimeType.split('/')[1] || 'png'}`;
      const file = new File([blob], fileName, { type: mimeType });

      handleUpload(file, 'users/avatar_icon', undefined, signal)
        .then((res) => {
          handleSetAvatarCb(res);
        })
        .catch((error) => {
          message.error(error?.message ?? 'Upload failed');
        })
        .finally(() => {
          setCropModalOpen(false);
          setIsUploading(false);
        });
    }, mimeType);
  });

  const FileInput = useMemo(() => {
    return (
      <input
        accept="image/png, image/jpeg"
        className="hidden"
        disabled={isUploading}
        onChange={onAvatarSelect}
        type="file"
      />
    );
  }, [onAvatarSelect]);

  const CropperModal = useMemo(() => {
    return (
      <Modal
        centered
        closeIcon={false}
        confirmLoading={isUploading}
        onCancel={() => setCropModalOpen(false)}
        onOk={() => {
          handleUploadAvatar();
        }}
        open={cropModalOpen}
        title={<div className="text-base leading-7 -mt-3 mb-2">{cropperModalTitle}</div>}
        zIndex={zIndex}
      >
        <Cropper
          ref={cropperRef}
          aspectRatio={1}
          autoCropArea={1}
          background={false}
          center={true}
          checkOrientation={false}
          cropBoxResizable={true}
          minCropBoxHeight={30}
          minCropBoxWidth={30}
          preview=".img-preview"
          ready={onReady}
          responsive={true}
          src={uploadAvatarFile}
          style={{ height: 400, width: '100%' }}
          viewMode={1}
          zoomTo={1}
        />
      </Modal>
    );
  }, [isUploading, cropModalOpen, cropperModalTitle, uploadAvatarFile, handleUploadAvatar, zIndex]);

  return {
    FileInput,
    CropperModal,
    isUploading
  };
};

export default useAvatarCropper;
